import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Menu, X, LayoutDashboard, Store, Shield, LogOut, Settings, Package, Bell } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/vaulted";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, hasRole, LOGO_URL } from "@/lib/api";
import { cn } from "@/lib/utils";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/categories", label: "Categories" },
  { to: "/become-a-seller", label: "Become a Seller" },
  { to: "/support", label: "Support" },
];

export const Avatar = ({ user, size = 8 }) => (
  user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: size * 4, height: size * 4 }} className="rounded-full object-cover" /> :
  <span style={{ width: size * 4, height: size * 4 }} className="grid place-items-center rounded-full bg-violet-600/20 text-xs font-semibold text-violet-300 ring-1 ring-violet-500/30">{user?.username?.[0]?.toUpperCase()}</span>
);

function UserMenu({ user }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full p-0.5 ring-1 ring-transparent hover:ring-violet-500/50 transition-[box-shadow] outline-none">
        <Avatar user={user} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-[#111116] border-[#27272A]">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm text-white">{user.username}</div>
          <div className="truncate text-xs text-zinc-500">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#27272A]" />
        <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/account")}><LayoutDashboard className="mr-2 h-4 w-4" />Dashboard</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/account/orders")}><Package className="mr-2 h-4 w-4" />My Orders</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-notifications" onClick={() => nav("/account/notifications")}><Bell className="mr-2 h-4 w-4" />Notifications</DropdownMenuItem>
        {hasRole(user, "SELLER") && <DropdownMenuItem data-testid="menu-seller" onClick={() => nav("/seller")}><Store className="mr-2 h-4 w-4" />Seller Studio</DropdownMenuItem>}
        {user?.account_type === "staff" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><Shield className="mr-2 h-4 w-4" />Admin</DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-settings" onClick={() => nav("/account/settings")}><Settings className="mr-2 h-4 w-4" />Account Settings</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#27272A]" />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }} className="text-red-400 focus:text-red-300"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchBox({ onDone }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav(`/marketplace?q=${encodeURIComponent(q)}`); onDone?.(); }} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
      <input autoFocus data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="v-input h-9 pl-9 w-full" />
    </form>
  );
}

export function Navbar() {
  const { user } = useAuth();
  const { count } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMobile(false); setSearchOpen(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#27272A]/80 bg-[#09090B]/85 backdrop-blur-xl" data-testid="navbar">
      <div className="container-v flex h-16 items-center gap-6">
        <Logo />
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => cn("relative rounded-md px-3 py-2 text-sm transition-colors", isActive ? "text-white" : "text-zinc-400 hover:text-white")}>
              {({ isActive }) => (<>{l.label}{isActive && <span className="absolute inset-x-3 -bottom-[13px] h-px bg-violet-500" />}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1.5 lg:ml-0">
          {searchOpen ? (
            <div className="hidden md:block w-64"><SearchBox onDone={() => setSearchOpen(false)} /></div>
          ) : null}
          <button data-testid="nav-search-button" onClick={() => setSearchOpen((s) => !s)} className="grid h-9 w-9 place-items-center rounded-md text-zinc-400 hover:bg-white/5 hover:text-white transition-colors" aria-label="Search">
            {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </button>
          <Link to="/cart" data-testid="nav-cart-button" className="relative grid h-9 w-9 place-items-center rounded-md text-zinc-400 hover:bg-white/5 hover:text-white transition-colors" aria-label="Cart">
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-violet-500 px-1 text-[10px] font-semibold text-white">{count}</span>}
          </Link>
          {user ? (
            <div className="ml-1"><UserMenu user={user} /></div>
          ) : user === false ? (
            <div className="hidden sm:flex items-center gap-1.5 ml-1">
              <Link to="/login" data-testid="nav-login-button" className="v-btn h-9 px-3 text-zinc-300 hover:text-white">Login</Link>
              <Link to="/register" data-testid="nav-register-button" className="v-btn-primary h-9">Register</Link>
            </div>
          ) : <div className="w-20" />}
          <button data-testid="nav-mobile-toggle" onClick={() => setMobile((m) => !m)} className="lg:hidden grid h-9 w-9 place-items-center rounded-md text-zinc-300 hover:bg-white/5">
            {mobile ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {searchOpen && <div className="md:hidden container-v pb-3"><SearchBox onDone={() => setSearchOpen(false)} /></div>}
      {mobile && (
        <div className="lg:hidden border-t border-[#27272A] bg-[#09090B]" data-testid="mobile-menu">
          <div className="container-v flex flex-col py-2">
            {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.end} className="py-2.5 text-sm text-zinc-300 hover:text-white">{l.label}</NavLink>)}
            {user === false && (
              <div className="flex gap-2 py-3">
                <Link to="/login" className="v-btn-ghost flex-1">Login</Link>
                <Link to="/register" className="v-btn-primary flex-1">Register</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Announcement() {
  const [a, setA] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/promotions/active").catch(() => null), api.get("/site").catch(() => null)]).then(([p, st]) => {
      const pa = p?.data?.announcement;
      setA(pa ? { enabled: true, text: pa.promo_code ? `${pa.title} — code ${pa.promo_code}` : pa.title, link: pa.link || (pa.landing_page ? `/promo/${pa.slug}` : "") } : st?.data?.announcement);
    });
  }, []);
  if (!a?.enabled || !a.text) return null;
  return (
    <div data-testid="announcement-bar" className="border-b border-violet-500/20 bg-violet-600/10 text-center text-xs text-violet-200 py-2 px-4">
      {a.link ? <a href={a.link} className="hover:text-white underline-offset-2 hover:underline">{a.text}</a> : a.text}
    </div>
  );
}

export function Footer() {
  const cols = [
    { title: "Marketplace", links: [["Marketplace", "/marketplace"], ["Categories", "/categories"], ["Become a Seller", "/become-a-seller"]] },
    { title: "Help", links: [["Support", "/support"], ["Discord", "/support#discord"], ["My Orders", "/account/orders"]] },
    { title: "Panels", links: [["Seller Panel", "/seller"], ["Staff Login", "/staff/login"], ["Customer Dashboard", "/account"]] },
    { title: "Legal", links: [["Terms of Service", "/legal/terms"], ["Privacy", "/legal/privacy"], ["Refund Policy", "/legal/refunds"]] },
  ];
  return (
    <footer className="mt-20 border-t border-[#27272A] bg-[#09090B]" data-testid="footer">
      <div className="container-v grid gap-10 py-12 md:grid-cols-[1.4fr_repeat(4,1fr)]">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm text-zinc-500">The marketplace for Fortnite accounts — stacked lockers, OG skins and instant delivery from verified sellers. Prices in AUD.</p>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-zinc-500">{c.title}</p>
            <ul className="space-y-2">
              {c.links.map(([l, to]) => <li key={l}><Link to={to} data-testid={`footer-link-${l.toLowerCase().replace(/ /g, "-")}`} className="text-sm v-link">{l}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[#1c1c20]">
        <div className="container-v flex flex-col gap-2 py-5 text-xs text-zinc-600 sm:flex-row sm:justify-between">
          <span>© {new Date().getFullYear()} Vaulted. All rights reserved. · <Link to="/legal/terms" data-testid="footer-tos-bottom" className="hover:text-white underline-offset-2 hover:underline">Terms of Service</Link></span>
          <span>Vaulted is not affiliated with, endorsed or sponsored by Epic Games. Fortnite is a trademark of Epic Games, Inc.</span>
        </div>
      </div>
    </footer>
  );
}

export function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Announcement />
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

export { LOGO_URL };
